<template>
  <section
    class="message-set"
    :class="UserClass(message.type)"
  >
    <div></div>
    <div class="message">
      <div
        class="nickname"
        v-show="isShowName"
      >{{ UserNameOnly(message.name) }}</div>
      <div class="message-item">
        <span v-if="isHistory">
          <div class="text" v-html="message.text"></div>
        </span>

        <span v-else-if="!isHistory && message.messageType === 'user'">
          <div class="text" v-html="message.text"></div>
        </span>

        <img
          v-else-if="!isHistory && message.messageType === 'file' && isImage(message)"
          :src="message.plainUrl"
          class="chat-image"
          @click="openFileOrVideo"
        />

        <img
          v-else-if="!isHistory && message.messageType === 'file' && isVideo(message) && videoThumbnail"
          :src="videoThumbnail"
          class="chat-thumbnail"
          @click="openFileOrVideo"
        />

        <div
          v-else-if="!isHistory && message.messageType === 'file' && isPdf(message)"
          class="chat-file-preview pdf-message"
        >
          <a :href="message.plainUrl" target="_blank" rel="noopener noreferrer">
            <i class="fas fa-file-pdf pdf-icon"></i>
            <span>{{ $t('dialog.chat-message.pdf.download') }}</span>
          </a>
        </div>

       <div
          v-else-if="!isHistory && message.messageType === 'file' && isDoc(message)"
          class="chat-file-preview doc-message"
        >
          <a :href="message.plainUrl" target="_blank" rel="noopener noreferrer">
            <i class="fas fa-file-word doc-icon"></i>
            <span>{{ $t('dialog.chat-message.file.type') }}</span>
          </a>
        </div>

        <div
          v-else-if="!isHistory && message.messageType === 'file' && isText(message)"
          class="chat-file-preview text-message"
        >
          <a :href="message.plainUrl" target="_blank" rel="noopener noreferrer">
            <i class="fas fa-file-alt text-icon"></i>
            <span>{{ $t('dialog.chat-message.file.type') }}</span>
          </a>
        </div>

        <div
          v-else-if="!isHistory && message.messageType === 'file' && isVideo(message)"
          class="chat-file-preview"
          @click="openFileOrVideo"
        >
          ▶ <span>{{ $t('dialog.chat-message.video.type') }}</span>
        </div>

        <div
          v-else-if="!isHistory && message.messageType === 'file'"
          class="chat-file-preview"
          @click="openFileOrVideo"
        >
          📄 <span>{{ $t('dialog.chat-message.file.type') }}</span>
        </div>

        <div v-else-if="!isHistory" class="text" v-html="message.text || 'Unknown message type'"></div>

        <div class="time">{{ MessageCreatedAt(message.createdAt) }}</div>
      </div>
    </div>
  </section>
</template>
<script>
import globals, {
  imageExtensions,
  videoExtensions,
  pdfExtensions,
  wordDocumentExtensions,
  otherDocumentExtensions,
  allFileExtensions
} from '@/plugins/globals.js'
var _checkTime = val => {
  return +val < 10 ? '0' + val : val
}

export default {
  name: 'chat-message',
  props: {
    message: Object,
    lastmessage: Object,
    isHistory: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      videoThumbnail: null
    }
  },
  computed: {
    isShowName() {
      let _show = true
      if (this.lastmessage !== null) {
        if (this.message.name === this.lastmessage.name) {
          _show = false
        }
      }
      return _show
    },
    isHistoryFileMessage() {
      if (!this.isHistory || !this.message.text) return false
      return this.checkFileType(this.message, allFileExtensions)
    }
  },
  watch: {
    'message.messageId': {
      immediate: true,
      handler() {
        if (this.isVideo(this.message) && !this.videoThumbnail) {
          this.generateVideoThumbnail(this.message.plainUrl)
        }
      }
    }
  },
  methods: {
    UserClass(type) {
      let _class = ''
      if (type === 'sent') {
        _class = 'user'
      }
      return _class
    },
    UserNameOnly(email) {
      let _userName = email
      if (!globals.isMahle()) {
        return _userName
      }
      let userName = email.substring(0, email.lastIndexOf('@'))
      if (userName !== '') {
        _userName = userName
      }
      return _userName
    },
    MessageCreatedAt(createdAt) {
      var _date = new Date(createdAt)
      var _time = _checkTime(_date.getHours()) + ':' + _checkTime(_date.getMinutes())
      return _time
    },
    generateVideoThumbnail(url) {
      const video = document.createElement('video')
      video.src = url
      video.crossOrigin = 'anonymous'
      video.muted = true
      video.playsInline = true

      video.addEventListener('loadedmetadata', () => {
        const seekTime = Math.min(1, video.duration / 2 || 1)
        video.currentTime = seekTime
      })

      video.addEventListener('seeked', () => {
        requestAnimationFrame(() => {
          const { videoWidth, videoHeight } = video
          if (!videoWidth || !videoHeight) {
            setTimeout(() => {
              this.generateVideoThumbnail(url) // retry once
            }, 300)
            return
          }
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          this.videoThumbnail = canvas.toDataURL('image/jpeg')
        })
      })

      video.addEventListener('error', (err) => {
        console.error('ERROR => video failed to load', err)
      })

      video.load()
    },
    isVideo(message) {
      return this.checkFileType(message, videoExtensions, 'video/')
    },
    isImage(message) {
      return this.checkFileType(message, imageExtensions, 'image/')
    },
    isPdf(message) {
      return this.checkFileType(message, pdfExtensions)
    },
    isDoc(message) {
      return this.checkFileType(message, wordDocumentExtensions)
    },
    isText(message) {
      return this.checkFileType(message, otherDocumentExtensions)
    },
    openFileOrVideo() {
      this.$emit('image-clicked', this.message.plainUrl)
    },
    isUnknownFile(message) {
      return message.messageType === 'file' &&
            !this.isImage(message) &&
            !this.isVideo(message) &&
            !this.isPdf(message) &&
            !this.isDoc(message) &&
            !this.isText(message)
    },
    isFileDeleted(message) {
      // Check if file is deleted (no plainUrl but has messageType 'file')
      return message.messageType === 'file' && (!message.plainUrl || message.plainUrl === '')
    },
    checkFileType(message, extensions, contentTypePrefix = null) {
      // Check contentType first for live messages
      if (contentTypePrefix && message.contentType) {
        return message.contentType.startsWith(contentTypePrefix)
      }
      // Check plainUrl for live messages
      if (message.plainUrl) {
        const url = message.plainUrl.toLowerCase()
        return extensions.some(ext => url.endsWith(`.${ext}`))
      }
      // Check filename for history messages
      if (this.isHistory && message.text) {
        const filename = message.text.toLowerCase().trim()
        // Only check if it looks like a filename (same logic as isHistoryFileMessage)
        const hasFileExtension = filename.split('.').length > 1 && filename.length > 4 && filename.length < 100 && !filename.includes(' ')
        if (hasFileExtension) {
          return extensions.some(ext => filename.endsWith(`.${ext}`))
        }
      }
      return false
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-message-text {
  white-space: pre-wrap;
}
.chat-thumbnail {
  width: 160px;
  height: 160px;
  object-fit: cover;
  cursor: pointer;
  border-radius: 8px;
}
.chat-image {
  max-width: 200px;
  max-height: 200px;
  width: auto;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  margin-top: 5px;
  cursor: pointer;
}
.chat-file-preview {
  color: #fff;
  background-color: #444;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-block;
}
.chat-file-preview.pdf-message {
  background-color: white;
  color: black;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.pdf-icon {
  color: #e43f27; /* Adobe red */
  font-size: 1.5em;
  margin-right: 8px;
}
.chat-file-deleted {
  color: #999;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 6px;
  font-style: italic;
  display: inline-block;
}

.chat-file-preview.doc-message {
  background-color: white;
  color: black;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-file-preview.text-message {
  background-color: white;
  color: black;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.doc-icon {
  color: #2b579a; /* Microsoft Word blue */
  font-size: 1.5em;
  margin-right: 8px;
}

.text-icon {
  color: #6c757d; /* Gray for text files */
  font-size: 1.5em;
  margin-right: 8px;
}

.doc-message a,
.text-message a {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

.doc-message a:hover,
.text-message a:hover {
  text-decoration: none;
  color: inherit;
  opacity: 0.8;
}
</style>



// WEBPACK FOOTER //
// src/components/chat/chat-message.vue